import { APPAREL_PREORDERS_ID } from "@faire/help-center-utils/articleIds";
import { Button } from "@faire/web--source/slate/Button";
import { Column } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import brandPortalProducts from "@faire/web-api--source/routes/www/brand-portal/my-shop/products";
import brandPortalShopSettings from "@faire/web-api--source/routes/www/brand-portal/my-shop/settings";
import supportArticle from "@faire/web-api--source/routes/www/support/articles/articleId";
import React, { JSX } from "react";

import { Bold } from "../common/Bold";
import { LiItem } from "../common/LiItem";

export const PreordersIA = () => {
  return (
    <Column>
      <Typography>
        <StrictLocalMsg
          defaultMessage="Offering pre-orders allows customers to place orders for products that will be shipped at a later date. This can be useful for gauging demand before products go into production."
          description={{
            text: "Introduction to pre-orders",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="To set up pre-orders, follow these steps:"
          description={{
            text: "Header for pre-order setup steps",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <ol>
        <LiItem step={1}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Navigate to the <LinkFormatting>Products</LinkFormatting> page."
              values={{
                LinkFormatting: ProductsLinkFormatting,
              }}
              description={{
                text: "First step to set up pre-orders",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={2}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Select a product listing, and go to <BoldFormatting>Product settings</BoldFormatting> within the product editor."
              values={{
                BoldFormatting,
              }}
              description={{
                text: "Second step to set up pre-orders",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={3}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Find the <BoldFormatting>Let retailers preorder this product</BoldFormatting> option in settings, and toggle it <BoldFormatting>On</BoldFormatting>."
              values={{
                BoldFormatting,
              }}
              description={{
                text: "Third step to set up pre-orders",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={4}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Enter the expected <BoldFormatting>Ship Date</BoldFormatting> range and, if desired, the <BoldFormatting>Deadline to place an order</BoldFormatting> using the calendar fields."
              values={{
                BoldFormatting,
              }}
              description={{
                text: "Fourth step to set up pre-orders",
              }}
            />
          </Typography>
        </LiItem>
      </ol>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="The <BoldFormatting>Deadline to place an order</BoldFormatting> field is optional. If you set a date, the pre-order listing will automatically deactivate once the deadline passes. If you leave this field blank, the listing will remain active, and you can continue to receive pre-orders."
          values={{
            BoldFormatting,
          }}
          description={{
            text: "Explanation of the deadline field",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="You can also choose to only offer pre-orders to qualified retailers—those who have met Faire’s wholesale requirements and are on your Faire customer list. This option is selected by default and can be managed in your <LinkFormatting>Shop settings</LinkFormatting> tab."
          values={{
            LinkFormatting: ShopSettingsLinkFormatting,
          }}
          description={{
            text: "Explanation of qualified retailers option",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="For more information on apparel-specific guidance, visit <LinkFormatting>Apparel Preorders</LinkFormatting>."
          values={{
            LinkFormatting: ArticleLinkFormatting,
          }}
          description={{
            text: "Link to apparel-specific guidance",
          }}
        />
      </Typography>
    </Column>
  );
};

const ProductsLinkFormatting = (chunks: JSX.Element) => (
  <Button variant="tertiary" linkTo={brandPortalProducts()} target="_blank">
    <Bold>{chunks}</Bold>
  </Button>
);

const ShopSettingsLinkFormatting = (chunks: JSX.Element) => (
  <Button variant="tertiary" linkTo={brandPortalShopSettings()} target="_blank">
    <Bold>{chunks}</Bold>
  </Button>
);

const ArticleLinkFormatting = (chunks: JSX.Element) => (
  <Button
    variant="tertiary"
    linkTo={supportArticle(APPAREL_PREORDERS_ID)}
    target="_blank"
  >
    {chunks}
  </Button>
);

const BoldFormatting = (chunks: JSX.Element) => <Bold>{chunks}</Bold>;
